import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ContainerCreateBody, LigneContainerInput } from '@cockpit/shared';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { formatEuro, parseDateToIso, parseEuroToCentimes } from '../../lib/format';

interface Props {
  submitting?: boolean;
  onSubmit: (body: ContainerCreateBody) => void;
  onCancel: () => void;
}

// Le taux de change est saisi en "1 USD = X EUR" avec 4 décimales (ex: 0,9234).
// Côté stockage : taux_change × 10000.
function parseTauxChange(input: string): number | null {
  const cleaned = input.trim().replace(/,/g, '.');
  if (cleaned === '') return null;
  const f = Number.parseFloat(cleaned);
  if (!Number.isFinite(f) || f <= 0) return null;
  return Math.round(f * 10000);
}

function formatTauxChangeForInput(centiBp: number): string {
  return (centiBp / 10000).toFixed(4).replace('.', ',');
}

function emptyLigne(): LigneContainerInput {
  return {
    reference_id: 0,
    largeur_cm: 400,
    longueur_rouleau_cm: 2500,
    nb_rouleaux: 1,
  };
}

export function ContainerForm({ submitting, onSubmit, onCancel }: Props): JSX.Element {
  const refsQuery = useQuery({
    queryKey: ['references', 'all'],
    queryFn: () => api.references.list(),
  });

  const [reference, setReference] = useState('');
  const [dateCommande, setDateCommande] = useState(new Date().toISOString().slice(0, 10));
  const [dateArrivee, setDateArrivee] = useState('');
  const [marchandisesUsd, setMarchandisesUsd] = useState('');
  const [transportUsd, setTransportUsd] = useState('');
  const [tauxChange, setTauxChange] = useState(formatTauxChangeForInput(9300));
  const [douane, setDouane] = useState('');
  const [tvaImport, setTvaImport] = useState('');
  const [dedouanement, setDedouanement] = useState('');
  const [portuaires, setPortuaires] = useState('');
  const [autres, setAutres] = useState('');
  const [financeGW, setFinanceGW] = useState(false);
  const [montantFinance, setMontantFinance] = useState('');
  const [notes, setNotes] = useState('');
  const [lignes, setLignes] = useState<LigneContainerInput[]>([emptyLigne()]);
  const [error, setError] = useState<string | null>(null);

  // Calcul coût total HT en live (HORS TVA)
  const coutTotalCalc = useMemo(() => {
    const m = parseEuroToCentimes(marchandisesUsd) ?? 0;
    const t = parseEuroToCentimes(transportUsd) ?? 0;
    const taux = parseTauxChange(tauxChange) ?? 0;
    const usdEur = Math.round(((m + t) * taux) / 10000);
    return (
      usdEur +
      (parseEuroToCentimes(douane) ?? 0) +
      (parseEuroToCentimes(dedouanement) ?? 0) +
      (parseEuroToCentimes(portuaires) ?? 0) +
      (parseEuroToCentimes(autres) ?? 0)
    );
  }, [marchandisesUsd, transportUsd, tauxChange, douane, dedouanement, portuaires, autres]);

  const m2Total = useMemo(() => {
    let s = 0;
    for (const l of lignes) {
      s += Math.round((l.longueur_rouleau_cm * l.largeur_cm) / 100) * l.nb_rouleaux;
    }
    return s;
  }, [lignes]);

  const coutParM2 = m2Total > 0 ? Math.round((coutTotalCalc * 100) / m2Total) : 0;

  const handleAddLigne = (): void => {
    setLignes([...lignes, emptyLigne()]);
  };
  const handleRemoveLigne = (i: number): void => {
    setLignes(lignes.filter((_, idx) => idx !== i));
  };
  const handleLigneChange = (i: number, partial: Partial<LigneContainerInput>): void => {
    setLignes(lignes.map((l, idx) => (idx === i ? { ...l, ...partial } : l)));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    const dc = parseDateToIso(dateCommande);
    if (dc === null) {
      setError('Date commande invalide.');
      return;
    }
    const da = dateArrivee === '' ? null : parseDateToIso(dateArrivee);
    const m = parseEuroToCentimes(marchandisesUsd) ?? 0;
    const t = parseEuroToCentimes(transportUsd) ?? 0;
    const taux = parseTauxChange(tauxChange) ?? 0;
    if (taux <= 0) {
      setError('Taux de change invalide.');
      return;
    }
    const lignesValides = lignes.filter((l) => l.reference_id > 0 && l.nb_rouleaux > 0);
    if (lignesValides.length === 0) {
      setError('Au moins une ligne de composition est requise.');
      return;
    }
    onSubmit({
      reference_container: reference.trim(),
      date_commande: dc,
      date_arrivee_prevue: da,
      marchandises_usd_centimes: m,
      transport_usd_centimes: t,
      taux_change_usd_eur: taux,
      douane_eur_centimes: parseEuroToCentimes(douane) ?? 0,
      tva_import_eur_centimes: parseEuroToCentimes(tvaImport) ?? 0,
      dedouanement_eur_centimes: parseEuroToCentimes(dedouanement) ?? 0,
      portuaires_eur_centimes: parseEuroToCentimes(portuaires) ?? 0,
      autres_eur_centimes: parseEuroToCentimes(autres) ?? 0,
      finance_global_wood: financeGW ? 1 : 0,
      montant_finance_eur_centimes: financeGW ? (parseEuroToCentimes(montantFinance) ?? 0) : 0,
      notes: notes.trim() === '' ? null : notes.trim(),
      lignes: lignesValides,
    });
  };

  const references = refsQuery.data?.items ?? [];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error !== null && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Référence container *"
          value={reference}
          onChange={(e): void => setReference(e.target.value)}
          required
          placeholder="Ex: Container Mai 2026"
          className="md:col-span-2"
        />
        <div />
        <Input
          label="Date commande *"
          type="date"
          value={dateCommande}
          onChange={(e): void => setDateCommande(e.target.value)}
          required
        />
        <Input
          label="Date arrivée prévue"
          type="date"
          value={dateArrivee}
          onChange={(e): void => setDateArrivee(e.target.value)}
        />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Coûts</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Marchandises USD"
            value={marchandisesUsd}
            onChange={(e): void => setMarchandisesUsd(e.target.value)}
          />
          <Input
            label="Transport USD"
            value={transportUsd}
            onChange={(e): void => setTransportUsd(e.target.value)}
          />
          <Input
            label="Taux change (1 USD = X EUR)"
            value={tauxChange}
            onChange={(e): void => setTauxChange(e.target.value)}
            hint="ex : 0,9300"
          />
          <Input
            label="Douane EUR"
            value={douane}
            onChange={(e): void => setDouane(e.target.value)}
          />
          <Input
            label="TVA import EUR (récup., hors coût HT)"
            value={tvaImport}
            onChange={(e): void => setTvaImport(e.target.value)}
          />
          <Input
            label="Dédouanement EUR"
            value={dedouanement}
            onChange={(e): void => setDedouanement(e.target.value)}
          />
          <Input
            label="Portuaires EUR"
            value={portuaires}
            onChange={(e): void => setPortuaires(e.target.value)}
          />
          <Input
            label="Autres EUR"
            value={autres}
            onChange={(e): void => setAutres(e.target.value)}
          />
        </div>
        <div className="mt-3 text-sm text-slate-400">
          Coût total HT (hors TVA récup.) :{' '}
          <span className="text-floryon-green font-semibold">{formatEuro(coutTotalCalc)}</span>
        </div>
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-200">Composition</h2>
          <Button type="button" size="sm" variant="secondary" onClick={handleAddLigne}>
            + Ligne
          </Button>
        </div>
        <div className="space-y-2">
          {lignes.map((l, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end bg-slate-900 border border-slate-800 rounded-md p-3"
            >
              <Select
                label="Référence *"
                value={String(l.reference_id)}
                onChange={(e): void =>
                  handleLigneChange(i, { reference_id: Number.parseInt(e.target.value, 10) })
                }
                options={[
                  { value: '0', label: '— Choisir —' },
                  ...references.map((r) => ({ value: String(r.id), label: r.nom })),
                ]}
              />
              <Select
                label="Largeur (cm)"
                value={String(l.largeur_cm)}
                onChange={(e): void =>
                  handleLigneChange(i, {
                    largeur_cm: Number.parseInt(e.target.value, 10) as 200 | 400,
                  })
                }
                options={[
                  { value: '200', label: '200' },
                  { value: '400', label: '400' },
                ]}
              />
              <Input
                label="Longueur (cm)"
                type="number"
                value={String(l.longueur_rouleau_cm)}
                onChange={(e): void =>
                  handleLigneChange(i, {
                    longueur_rouleau_cm: Number.parseInt(e.target.value, 10) || 0,
                  })
                }
              />
              <Input
                label="Nb rouleaux"
                type="number"
                value={String(l.nb_rouleaux)}
                onChange={(e): void =>
                  handleLigneChange(i, { nb_rouleaux: Number.parseInt(e.target.value, 10) || 0 })
                }
              />
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={(): void => handleRemoveLigne(i)}
                disabled={lignes.length <= 1}
              >
                Retirer
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-3 text-sm text-slate-400">
          m² total prévu :{' '}
          <span className="text-slate-200 font-semibold">
            {(m2Total / 100).toFixed(2).replace('.', ',')} m²
          </span>
          {' · '}
          Coût €/m² prévisionnel :{' '}
          <span className="text-slate-200 font-semibold">{formatEuro(coutParM2)}/m²</span>
        </div>
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Financement</h2>
        <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <input
            type="checkbox"
            checked={financeGW}
            onChange={(e): void => setFinanceGW(e.target.checked)}
          />
          Financé par Global Wood
        </label>
        {financeGW && (
          <Input
            label="Montant financé EUR"
            value={montantFinance}
            onChange={(e): void => setMontantFinance(e.target.value)}
          />
        )}
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <label className="text-xs font-medium text-slate-300">Notes</label>
        <textarea
          value={notes}
          onChange={(e): void => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
        />
      </section>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting === true ? 'Création…' : 'Créer le container'}
        </Button>
      </div>
    </form>
  );
}
